import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button, Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { Company } from '../../../model/company/Company';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { getCorrectSocialUrl } from '../../../utils/Functions';
import { CompanyService } from '../../company/CompanyService';
import { OnboardingService } from '../OnboardingService';
import OnboardingFilters from './OnboardingFilters';
import { IUser } from '../../../model/user/IUser';
import { Platform } from '../../../model/platform/Platform';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { Campaign } from '../../../model/campaign/Campaign';
import { FiInfo } from 'react-icons/fi';
import { FaCheck, FaEdit, FaTimes } from 'react-icons/fa';
import { CustomImageHandler } from '../../../shared/components/img/CustomImageHandler';
import ModalForm from '../../../shared/components/modal/ModalForm';
import TableLayout from '../../../shared/layout/table/TableLayout';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';

interface IRequest {
    id: number;
    fullName: string;
    email: string;
    companyId: number;
    isOnboarded: boolean;
    isContacted: boolean;
    photoUrl: string;
    displayName: string;
    requestedBy: string;
    title: string;
    internalUser: IUser;
    mainPlatform: Platform;
    externalLink: string;
    campaign: Campaign;
    isDeclined: boolean;
    externalRequestId: number;
}
const ONBOARDING_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Photo' },
    { label: 'Platform' },
    { label: 'Name' },
    { label: 'Email' },
    { label: 'isContacted?' },
    { label: 'Onboarding Declined' },
    { label: 'Handle' },
    { label: 'Requested by' },
    { label: 'Campaign' },
    { label: 'isOnboarded' },
    { label: 'Onboarded user id' },
];

const OnboardingRequestList = () => {
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
        handle: '',
        companyId: null,
        campaignId: null,
        isOnboarded: false,
        isContacted: false,
        isDeclined: false,
        trigger: false,
    });
    const [totalPages, setTotalPages] = useState(0);
    const lastQueryKeyRef = useRef<string | null>(null);
    const [company, setCompany] = useState<Company[]>([]);
    const [campaigns] = useState<Campaign[]>([]);
    const [requestedList, setRequestedList] = useState<IRequest[]>([]);
    const [emailData, setEmailData] = useState<{ email: string; id: number | undefined }>({ email: '', id: undefined });
    const [emailDataError, setEmailDataError] = useState<{ email: string }>({ email: '' });
    const [showModalEmail, setShowModalEmail] = useState(false);
    const { trigger, ...query } = pagination;
    const queryKey = JSON.stringify(query);

    const handlePages = (updatePage: number) => {
        setPagination({ ...pagination, page: updatePage });
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value, name } = e.target;
        setEmailData(prev => ({ ...prev, [name]: value }));

        if (name === 'email') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(value)) {
                setEmailDataError(prev => ({ ...prev, [name]: 'Invalid email format' }));
            } else {
                setEmailDataError(prev => ({ ...prev, [name]: '' }));
            }
        }
    };

    useEffect(() => {
        CompanyService.getCompanies({ page: 1, perPage: 10, hasOnboardingRequests: true })
            .then(data => {
                setCompany(data.data);
            })
            .catch(err => ErrorToast(err));
    }, []);

    const editIsContacted = (id: number) => {
        OnboardingService.updateIsContacted(id)
            .then(data => {
                const updatedReqs = requestedList.map(prev => {
                    if (prev?.id === id) return data;
                    return prev;
                });
                setRequestedList(updatedReqs);
                SuccessToast('Successfully action');
            })
            .catch(err => ErrorToast(err));
    };

    const editIsDeclined = (id: number) => {
        OnboardingService.updateIsDeclined(id)
            .then(data => {
                const updatedReqs = requestedList.map(prev => {
                    if (prev?.id === id) return data;
                    return prev;
                });
                setRequestedList(updatedReqs);
                SuccessToast('Successfully action');
                setEmailData({ email: '', id: undefined });
                setEmailDataError({ email: '' });
            })
            .catch(err => ErrorToast(err));
    };

    const handleSubmitEmail = () => {
        OnboardingService.updateEmail(emailData.id as number, emailData)
            .then(() => {
                setPagination(prev => ({ ...prev, trigger: !prev.trigger }));
                SuccessToast('Successfully action');
                setShowModalEmail(false);
            })
            .catch(err => ErrorToast(err));
    };

    useEffect(() => {
        let isCurrent = true;
        const isNewQuery = lastQueryKeyRef.current !== queryKey;
        lastQueryKeyRef.current = queryKey;

        if (isNewQuery) setDataLoading(true);

        OnboardingService.getAllRequests(pagination)
            .then(data => {
                if (!isCurrent) return;
                setRequestedList(data?.data);
                setTotalPages(Math.ceil(data.count / pagination.perPage));
            })
            .catch(err => {
                if (isCurrent) ErrorToast(err);
            })
            .finally(() => {
                if (isCurrent) setDataLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [queryKey, trigger]);
    return (
        <>
            <ModalForm
                title="Edit email"
                show={showModalEmail}
                size="sm"
                contentBody={
                    <div>
                        <Form.Control
                            isInvalid={!!emailDataError.email}
                            value={emailData.email}
                            name="email"
                            onChange={handleEmailChange}
                        />
                        <Form.Control.Feedback type="invalid">{emailDataError.email}</Form.Control.Feedback>
                    </div>
                }
                onClose={() => {
                    setShowModalEmail(false);
                    setEmailData({ email: '', id: undefined });
                    setEmailDataError({ email: '' });
                }}
                disableSave={!emailData.email || !!emailDataError.email}
                onSave={handleSubmitEmail}
            />
            <OnboardingFilters
                company={company}
                campaign={campaigns}
                setPagination={setPagination}
                pagination={pagination}
            />
            <TableLayout
                listLength={requestedList.length}
                handlePages={handlePages}
                pagination={{ ...pagination, totalPages }}
                isLoading={dataLoading}
                dataName={'requests'}
                skeletonColumns={ONBOARDING_COLUMNS}
                skeletonRows={pagination.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {ONBOARDING_COLUMNS.map(column => (
                                <th key={column.label}>{column.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {requestedList?.map(request => {
                            return (
                                <tr key={request.id}>
                                    <td>{request?.id}</td>
                                    <td>
                                        <CustomImageHandler
                                            altTag={''}
                                            classes="rounded-circle"
                                            thumbnailPath={request?.photoUrl ?? ''}
                                            photoPath={request?.photoUrl ?? ''}
                                            style={{ width: 50, height: 50 }}
                                        />
                                    </td>
                                    <td>
                                        <img
                                            alt={request.mainPlatform.name ?? ''}
                                            src={request?.mainPlatform?.path || '/images/placeholder.png'}
                                            style={{ width: 35, height: 35 }}
                                        />
                                    </td>
                                    <td>{request?.fullName ?? '-'}</td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            {request?.email || '-'}
                                            {!request.internalUser?.id && (
                                                <Button
                                                    variant=""
                                                    title="Edit email"
                                                    className="btn-icon"
                                                    onClick={() => {
                                                        setShowModalEmail(true);
                                                        setEmailData(prev => ({
                                                            ...prev,
                                                            email: request.email ?? '',
                                                            id: request.externalRequestId,
                                                        }));
                                                    }}
                                                >
                                                    <FaEdit />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <Form.Check
                                            defaultChecked={request.isContacted}
                                            onChange={() => {
                                                editIsContacted(request?.id);
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Check
                                            defaultChecked={request.isDeclined}
                                            onChange={() => {
                                                editIsDeclined(request?.id);
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <a
                                            target="_blank"
                                            className="d-flex align-items-center mb-2 mr-3"
                                            href={`${getCorrectSocialUrl(
                                                request?.mainPlatform?.platformEnum as string,
                                                request?.title,
                                                request?.externalLink
                                            )}`}
                                            rel="noreferrer"
                                        >
                                            <span className="ml-1 mb-0">@{request?.title ?? '-'}</span>
                                        </a>
                                    </td>
                                    <td>{request?.requestedBy ?? '-'}</td>
                                    <td>
                                        {request?.campaign?.id}
                                        {request?.campaign?.id && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id="campaign-tooltip">{request?.campaign?.title}</Tooltip>
                                                }
                                            >
                                                <span className="ml-1">
                                                    <FiInfo size={14} className="cursor-pointer" />
                                                </span>
                                            </OverlayTrigger>
                                        )}
                                    </td>
                                    <td>
                                        {request?.isOnboarded ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {!!request?.internalUser?.id && (
                                            <div>
                                                <ImpersonateEye id={request?.internalUser?.id} />
                                                {request?.internalUser?.id}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
};

export default OnboardingRequestList;
